import { useState, useRef } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Plus, Search, Pencil, X, ChevronLeft, ChevronRight,
  Upload, FileText, Trash2, History, Eye,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const PAGE_SIZE = 50;

const TIPO_ARQUIVO_LABELS: Record<string, string> = {
  cpf: "CPF",
  rg: "RG",
  pis: "PIS",
  contrato: "Contrato",
  exame: "Exame",
  outros: "Outros",
};

const CAMPOS_HISTORICO_LABELS: Record<string, string> = {
  cargoId: "Cargo",
  status: "Status",
  dataDesligamento: "Data de Desligamento",
  supervisorId: "Supervisor",
  possuiSupervisao: "Possui Supervisão",
  setor: "Setor",
  vrDia: "VR/dia",
  vtDia: "VT/dia",
  convenioSaude: "Convênio Saúde",
  convenioOdonto: "Convênio Odonto",
  auxilioFaculdade: "Auxílio Faculdade",
  auxilioCurso: "Auxílio Curso",
  outrosBeneficios: "Outros Benefícios",
  beneficioDataInicio: "Início Benefício",
  beneficioDataFim: "Fim Benefício",
  diasTrabalho: "Dias de Trabalho",
};

const EMPTY_FORM = {
  emitenteId: 0,
  nome: "",
  cpf: "",
  rg: "",
  pis: "",
  dataNascimento: "",
  dataAdmissao: "",
  dataDesligamento: "",
  status: "ativo" as "ativo" | "inativo",
  cargoId: "",
  setor: "",
  centroCusto: "",
  possuiSupervisao: false,
  supervisorId: "",
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  estado: "",
  vrDia: "",
  vtDia: "",
  convenioSaude: "",
  convenioOdonto: "",
  beneficioDataInicio: "",
  beneficioDataFim: "",
  bonificacaoMin: "",
  bonificacaoMax: "",
  regrasBonificacao: "",
  auxilioFaculdade: "",
  auxilioCurso: "",
  outrosBeneficios: "",
  descricaoOutrosBeneficios: "",
  observacoesInternas: "",
  horarioEntrada: "",
  horarioSaida: "",
  horarioInicioAlmoco: "",
  horarioFimAlmoco: "",
  cargaHorariaDiaria: "",
  diasTrabalho: [] as number[],
};

function parseDecimal(v: string): number | undefined {
  if (v === "" || v === null || v === undefined) return undefined;
  const n = parseFloat(v);
  return isNaN(n) ? undefined : n;
}

function parseDiasTrabalho(v: unknown): number[] {
  if (!v) return [];
  try {
    const dias = Array.isArray(v) ? v : JSON.parse(String(v));
    return Array.isArray(dias)
      ? dias.filter((dia) => Number.isInteger(dia) && dia >= 0 && dia <= 6)
      : [];
  } catch {
    return [];
  }
}

function formatCurrency(v: any) {
  if (v == null || v === "") return "—";
  return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(v: any) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("pt-BR");
}

export default function DepartamentoPessoal() {
  const { user } = useGoogleAuth();
  const isAdmin = user?.role === "admin";
  const isCliente = user?.role === "cliente";

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("todos");
  const [filterSetor, setFilterSetor] = useState<string>("todos");
  const [filterCargoId, setFilterCargoId] = useState<string>("todos");
  const [filterSupervisorId, setFilterSupervisorId] = useState<string>("todos");
  const [filterPossuiSupervisao, setFilterPossuiSupervisao] = useState<string>("todos");
  const [filterEmitenteId, setFilterEmitenteId] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  // Colunas visíveis
  const [colsVisiveis, setColsVisiveis] = useState({
    nome: true, cargo: true, setor: true, supervisor: true,
    status: true, salario: true, admissao: true, emitente: true,
  });

  // Modais
  const [modalFormOpen, setModalFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [modalDetalheOpen, setModalDetalheOpen] = useState(false);
  const [detalheId, setDetalheId] = useState<number | null>(null);

  // Upload
  const [uploadTipo, setUploadTipo] = useState<string>("outros");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Queries
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, { enabled: !!user });
  const { data: cargos = [] } = trpc.rh.cargos.list.useQuery({ ativo: true }, { enabled: !!user });
  const { data: funcionariosAtivos = [] } = trpc.rh.funcionarios.listAtivos.useQuery(
    { emitenteIdFiltro: filterEmitenteId ?? undefined },
    { enabled: !!user }
  );

  const queryParams = {
    emitenteIdFiltro: filterEmitenteId ?? undefined,
    search: search || undefined,
    status: filterStatus !== "todos" ? filterStatus : undefined,
    setor: filterSetor !== "todos" ? filterSetor : undefined,
    cargoId: filterCargoId !== "todos" ? parseInt(filterCargoId) : undefined,
    supervisorId: filterSupervisorId !== "todos" ? parseInt(filterSupervisorId) : undefined,
    possuiSupervisao: filterPossuiSupervisao !== "todos" ? filterPossuiSupervisao === "sim" : undefined,
    page,
    pageSize: PAGE_SIZE,
  };

  const { data: listData, refetch: refetchList } = trpc.rh.funcionarios.list.useQuery(queryParams, { enabled: !!user });
  const funcionarios: any[] = listData?.data ?? [];
  const total: number = listData?.total ?? 0;
  const totalPages: number = listData?.totalPages ?? 1;

  const { data: detalhe, refetch: refetchDetalhe } = trpc.rh.funcionarios.getById.useQuery(
    { id: detalheId! },
    { enabled: detalheId !== null }
  );
  const { data: arquivos = [], refetch: refetchArquivos } = trpc.rh.funcionarios.arquivos.list.useQuery(
    { funcionarioId: detalheId! },
    { enabled: detalheId !== null }
  );
  const { data: historico = [] } = trpc.rh.funcionarios.historico.list.useQuery(
    { funcionarioId: detalheId! },
    { enabled: detalheId !== null }
  );

  const createMut = trpc.rh.funcionarios.create.useMutation({
    onSuccess: () => { toast.success("Funcionário cadastrado"); refetchList(); fecharModalForm(); },
    onError: (e) => toast.error(e.message),
  });
  const updateMut = trpc.rh.funcionarios.update.useMutation({
    onSuccess: () => { toast.success("Funcionário atualizado"); refetchList(); if (detalheId) refetchDetalhe(); fecharModalForm(); },
    onError: (e) => toast.error(e.message),
  });
  const addArquivoMut = trpc.rh.funcionarios.arquivos.add.useMutation({
    onSuccess: () => { toast.success("Arquivo adicionado"); refetchArquivos(); },
    onError: (e) => toast.error(e.message),
  });
  const deleteArquivoMut = trpc.rh.funcionarios.arquivos.delete.useMutation({
    onSuccess: () => { toast.success("Arquivo removido"); refetchArquivos(); },
    onError: (e) => toast.error(e.message),
  });

  // Setores únicos do banco (não da página atual)
  const { data: setoresUnicos = [] } = trpc.rh.funcionarios.listSetores.useQuery(
    { emitenteIdFiltro: filterEmitenteId ?? undefined },
    { enabled: !!user }
  );

  function toDateInput(v: any): string {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  }
  function abrirNovo() {
    setEditId(null);
    const emitenteIdPadrao = isCliente && (emitentes as any[]).length > 0 ? (emitentes as any[])[0].id : 0;
    setForm({ ...EMPTY_FORM, emitenteId: emitenteIdPadrao });
    setModalFormOpen(true);
  }

  function abrirEditar(f: any) {
    setEditId(f.id);
    setForm({
      emitenteId: f.emitenteId,
      nome: f.nome ?? "",
      cpf: f.cpf ?? "",
      rg: f.rg ?? "",
      pis: f.pis ?? "",
      dataNascimento: toDateInput(f.dataNascimento),
      dataAdmissao: toDateInput(f.dataAdmissao),
      dataDesligamento: toDateInput(f.dataDesligamento),
      status: f.status ?? "ativo",
      cargoId: f.cargoId != null ? String(f.cargoId) : "",
      setor: f.setor ?? "",
      centroCusto: f.centroCusto ?? "",
      possuiSupervisao: !!f.possuiSupervisao,
      supervisorId: f.supervisorId != null ? String(f.supervisorId) : "",
      cep: f.cep ?? "",
      logradouro: f.logradouro ?? "",
      numero: f.numero ?? "",
      complemento: f.complemento ?? "",
      bairro: f.bairro ?? "",
      cidade: f.cidade ?? "",
      estado: f.estado ?? "",
      vrDia: f.vrDia != null ? String(f.vrDia) : "",
      vtDia: f.vtDia != null ? String(f.vtDia) : "",
      convenioSaude: f.convenioSaude != null ? String(f.convenioSaude) : "",
      convenioOdonto: f.convenioOdonto != null ? String(f.convenioOdonto) : "",
      beneficioDataInicio: toDateInput(f.beneficioDataInicio),
      beneficioDataFim: toDateInput(f.beneficioDataFim),
      bonificacaoMin: f.bonificacaoMin != null ? String(f.bonificacaoMin) : "",
      bonificacaoMax: f.bonificacaoMax != null ? String(f.bonificacaoMax) : "",
      regrasBonificacao: f.regrasBonificacao ?? "",
      auxilioFaculdade: f.auxilioFaculdade != null ? String(f.auxilioFaculdade) : "",
      auxilioCurso: f.auxilioCurso != null ? String(f.auxilioCurso) : "",
      outrosBeneficios: f.outrosBeneficios != null ? String(f.outrosBeneficios) : "",
      descricaoOutrosBeneficios: f.descricaoOutrosBeneficios ?? "",
      observacoesInternas: f.observacoesInternas ?? "",
      horarioEntrada: f.horarioEntrada ?? "",
      horarioSaida: f.horarioSaida ?? "",
      horarioInicioAlmoco: f.horarioInicioAlmoco ?? "",
      horarioFimAlmoco: f.horarioFimAlmoco ?? "",
      cargaHorariaDiaria: f.cargaHorariaDiaria != null ? String(f.cargaHorariaDiaria) : "",
      diasTrabalho: parseDiasTrabalho(f.diasTrabalho),
    });
    setModalFormOpen(true);
  }

  function fecharModalForm() {
    setModalFormOpen(false);
    setEditId(null);
    setForm({ ...EMPTY_FORM });
  }

  function abrirDetalhe(f: any) {
    setDetalheId(f.id);
    setModalDetalheOpen(true);
  }

  function fecharDetalhe() {
    setModalDetalheOpen(false);
    setDetalheId(null);
  }

  function handleSalvar() {
    if (!form.nome.trim()) return toast.error("Nome é obrigatório");
    if (!form.emitenteId) return toast.error("Emitente é obrigatório");

    const payload: any = {
      emitenteId: Number(form.emitenteId),
      nome: form.nome.trim(),
      cpf: form.cpf || undefined,
      rg: form.rg || undefined,
      pis: form.pis || undefined,
      dataNascimento: form.dataNascimento || undefined,
      dataAdmissao: form.dataAdmissao || undefined,
      dataDesligamento: form.dataDesligamento || null,
      status: form.status,
      cargoId: form.cargoId ? parseInt(form.cargoId) : undefined,
      setor: form.setor || undefined,
      centroCusto: form.centroCusto || undefined,
      possuiSupervisao: form.possuiSupervisao,
      supervisorId: form.supervisorId ? parseInt(form.supervisorId) : null,
      cep: form.cep || undefined,
      logradouro: form.logradouro || undefined,
      numero: form.numero || undefined,
      complemento: form.complemento || undefined,
      bairro: form.bairro || undefined,
      cidade: form.cidade || undefined,
      estado: form.estado || undefined,
      vrDia: parseDecimal(form.vrDia),
      vtDia: parseDecimal(form.vtDia),
      convenioSaude: parseDecimal(form.convenioSaude),
      convenioOdonto: parseDecimal(form.convenioOdonto),
      beneficioDataInicio: form.beneficioDataInicio || null,
      beneficioDataFim: form.beneficioDataFim || null,
      bonificacaoMin: parseDecimal(form.bonificacaoMin),
      bonificacaoMax: parseDecimal(form.bonificacaoMax),
      regrasBonificacao: form.regrasBonificacao || undefined,
      auxilioFaculdade: parseDecimal(form.auxilioFaculdade),
      auxilioCurso: parseDecimal(form.auxilioCurso),
      outrosBeneficios: parseDecimal(form.outrosBeneficios),
      descricaoOutrosBeneficios: form.descricaoOutrosBeneficios || undefined,
      observacoesInternas: form.observacoesInternas || undefined,
      horarioEntrada: form.horarioEntrada || null,
      horarioSaida: form.horarioSaida || null,
      horarioInicioAlmoco: form.horarioInicioAlmoco || null,
      horarioFimAlmoco: form.horarioFimAlmoco || null,
      cargaHorariaDiaria: parseDecimal(form.cargaHorariaDiaria),
      diasTrabalho: form.diasTrabalho.length > 0 ? form.diasTrabalho : undefined,
    };

    if (editId !== null) {
      updateMut.mutate({ id: editId, ...payload });
    } else {
      createMut.mutate(payload);
    }
  }

  async function handleUploadArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !detalheId) return;
    if (file.size > 50 * 1024 * 1024) return toast.error("Arquivo excede 50 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", body: fd });
      if (!res.ok) throw new Error("Falha no upload");
      const data = await res.json();
      await addArquivoMut.mutateAsync({
        funcionarioId: detalheId,
        tipo: uploadTipo as any,
        nome: file.name,
        url: data.url,
        tamanho: file.size,
      });
    } catch (err: any) {
      toast.error(err.message ?? "Erro no upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  // Cargo selecionado para exibir salário readonly
  const cargoSelecionado = form.cargoId
    ? (cargos as any[]).find((c) => c.id === parseInt(form.cargoId))
    : null;

  const isBusy = createMut.isPending || updateMut.isPending;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Departamento Pessoal</h1>
          <p className="text-sm text-muted-foreground">{total} funcionário{total !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={abrirNovo}>
          <Plus className="w-4 h-4 mr-2" /> Novo Funcionário
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar por nome, matrícula ou CPF..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        {!isCliente && (
          <Select value={filterEmitenteId ? String(filterEmitenteId) : "todos"} onValueChange={(v) => { setFilterEmitenteId(v === "todos" ? null : parseInt(v)); setPage(1); }}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Emitente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os emitentes</SelectItem>
              {(emitentes as any[]).map((e: any) => (
                <SelectItem key={e.id} value={String(e.id)}>{e.nomeFantasia || e.razaoSocial}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
          <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos status</SelectItem>
            <SelectItem value="ativo">Ativo</SelectItem>
            <SelectItem value="inativo">Inativo</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterSetor} onValueChange={(v) => { setFilterSetor(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Setor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos setores</SelectItem>
            {setoresUnicos.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterCargoId} onValueChange={(v) => { setFilterCargoId(v); setPage(1); }}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Cargo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos cargos</SelectItem>
            {(cargos as any[]).map((c: any) => <SelectItem key={c.id} value={String(c.id)}>{c.nome}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterPossuiSupervisao} onValueChange={(v) => { setFilterPossuiSupervisao(v); setPage(1); }}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Supervisão" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Supervisão: todos</SelectItem>
            <SelectItem value="sim">Com supervisão</SelectItem>
            <SelectItem value="nao">Sem supervisão</SelectItem>
          </SelectContent>
        </Select>

        {(filterStatus !== "todos" || filterSetor !== "todos" || filterCargoId !== "todos" || filterPossuiSupervisao !== "todos" || filterEmitenteId || search) && (
          <Button variant="ghost" size="sm" onClick={() => {
            setSearch(""); setFilterStatus("todos"); setFilterSetor("todos");
            setFilterCargoId("todos"); setFilterSupervisorId("todos");
            setFilterPossuiSupervisao("todos"); setFilterEmitenteId(null); setPage(1);
          }}>
            <X className="w-3 h-3 mr-1" /> Limpar
          </Button>
        )}
      </div>

      {/* Tabela */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {colsVisiveis.nome && <TableHead>Nome</TableHead>}
              {colsVisiveis.cargo && <TableHead>Cargo</TableHead>}
              {colsVisiveis.setor && <TableHead>Setor</TableHead>}
              {colsVisiveis.supervisor && <TableHead>Supervisor</TableHead>}
              {colsVisiveis.status && <TableHead>Status</TableHead>}
              {colsVisiveis.salario && <TableHead>Salário</TableHead>}
              {colsVisiveis.admissao && <TableHead>Admissão</TableHead>}
              {colsVisiveis.emitente && !isCliente && <TableHead>Emitente</TableHead>}
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {funcionarios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                  Nenhum funcionário encontrado
                </TableCell>
              </TableRow>
            ) : (
              funcionarios.map((f) => (
                <TableRow key={f.id}>
                  {colsVisiveis.nome && (
                    <TableCell>
                      <div className="font-medium">{f.nome}</div>
                      <div className="text-xs text-muted-foreground font-mono">{f.matricula}</div>
                    </TableCell>
                  )}
                  {colsVisiveis.cargo && <TableCell>{f.cargoNome ?? "—"}</TableCell>}
                  {colsVisiveis.setor && <TableCell>{f.setor ?? "—"}</TableCell>}
                  {colsVisiveis.supervisor && <TableCell>{f.supervisorNome ?? "—"}</TableCell>}
                  {colsVisiveis.status && (
                    <TableCell>
                      <Badge variant={f.status === "ativo" ? "default" : "secondary"}>
                        {f.status === "ativo" ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                  )}
                  {colsVisiveis.salario && <TableCell>{formatCurrency(f.cargoSalario)}</TableCell>}
                  {colsVisiveis.admissao && <TableCell>{formatDate(f.dataAdmissao)}</TableCell>}
                  {colsVisiveis.emitente && !isCliente && (
                    <TableCell className="text-sm">{f.emitenteNomeFantasia || f.emitenteRazaoSocial || "—"}</TableCell>
                  )}
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" title="Ver detalhe" onClick={() => abrirDetalhe(f)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" title="Editar" onClick={() => abrirEditar(f)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Página {page} de {totalPages} — {total} registros</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modal Formulário */}
      <Dialog open={modalFormOpen} onOpenChange={(v) => { if (!v) fecharModalForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="break-words">
              {editId !== null ? "Editar Funcionário" : "Novo Funcionário"}
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="dados" className="pt-2">
            <TabsList className="mb-4">
              <TabsTrigger value="dados">Dados Pessoais</TabsTrigger>
              <TabsTrigger value="endereco">Endereço</TabsTrigger>
              <TabsTrigger value="cargo">Cargo e Benefícios</TabsTrigger>
              <TabsTrigger value="outros">Outros</TabsTrigger>
            </TabsList>

            {/* Aba Dados Pessoais */}
            <TabsContent value="dados" className="space-y-4">
              {!isCliente && (
                <div className="space-y-1">
                  <Label>Emitente *</Label>
                  <Select
                    value={form.emitenteId ? String(form.emitenteId) : ""}
                    onValueChange={(v) => setForm((f) => ({ ...f, emitenteId: parseInt(v) }))}
                    disabled={isCliente}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
                    <SelectContent>
                      {(emitentes as any[]).map((e: any) => (
                        <SelectItem key={e.id} value={String(e.id)}>{e.nomeFantasia || e.razaoSocial}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label>Nome *</Label>
                <Input value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} placeholder="Nome completo" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>CPF</Label>
                  <Input value={form.cpf} onChange={(e) => setForm((f) => ({ ...f, cpf: e.target.value }))} placeholder="000.000.000-00" />
                </div>
                <div className="space-y-1">
                  <Label>RG</Label>
                  <Input value={form.rg} onChange={(e) => setForm((f) => ({ ...f, rg: e.target.value }))} placeholder="00.000.000-0" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>PIS</Label>
                  <Input value={form.pis} onChange={(e) => setForm((f) => ({ ...f, pis: e.target.value }))} placeholder="000.00000.00-0" />
                </div>
                <div className="space-y-1">
                  <Label>Data de Nascimento</Label>
                  <Input type="date" value={form.dataNascimento} onChange={(e) => setForm((f) => ({ ...f, dataNascimento: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Data de Admissão</Label>
                  <Input type="date" value={form.dataAdmissao} onChange={(e) => setForm((f) => ({ ...f, dataAdmissao: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v as any }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="inativo">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.status === "inativo" && (
                <div className="space-y-1">
                  <Label>Data de Desligamento</Label>
                  <Input type="date" value={form.dataDesligamento} onChange={(e) => setForm((f) => ({ ...f, dataDesligamento: e.target.value }))} />
                </div>
              )}
            </TabsContent>

            {/* Aba Endereço */}
            <TabsContent value="endereco" className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>CEP</Label>
                  <Input value={form.cep} onChange={(e) => setForm((f) => ({ ...f, cep: e.target.value }))} placeholder="00000-000" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label>Logradouro</Label>
                  <Input value={form.logradouro} onChange={(e) => setForm((f) => ({ ...f, logradouro: e.target.value }))} placeholder="Rua, Avenida..." />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Número</Label>
                  <Input value={form.numero} onChange={(e) => setForm((f) => ({ ...f, numero: e.target.value }))} />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label>Complemento</Label>
                  <Input value={form.complemento} onChange={(e) => setForm((f) => ({ ...f, complemento: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Bairro</Label>
                  <Input value={form.bairro} onChange={(e) => setForm((f) => ({ ...f, bairro: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Cidade</Label>
                  <Input value={form.cidade} onChange={(e) => setForm((f) => ({ ...f, cidade: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Estado (UF)</Label>
                  <Input value={form.estado} onChange={(e) => setForm((f) => ({ ...f, estado: e.target.value.toUpperCase().slice(0, 2) }))} placeholder="SP" maxLength={2} />
                </div>
              </div>
            </TabsContent>

            {/* Aba Cargo e Benefícios */}
            <TabsContent value="cargo" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Cargo</Label>
                  <Select value={form.cargoId || "none"} onValueChange={(v) => setForm((f) => ({ ...f, cargoId: v === "none" ? "" : v }))}>
                    <SelectTrigger><SelectValue placeholder="Selecione o cargo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sem cargo</SelectItem>
                      {(cargos as any[]).map((c: any) => (
                        <SelectItem key={c.id} value={String(c.id)}>{c.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Salário (somente leitura — vem do cargo)</Label>
                  <Input
                    value={cargoSelecionado?.salario != null ? formatCurrency(cargoSelecionado.salario) : "—"}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Setor</Label>
                  <Input value={form.setor} onChange={(e) => setForm((f) => ({ ...f, setor: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Centro de Custo</Label>
                  <Input value={form.centroCusto} onChange={(e) => setForm((f) => ({ ...f, centroCusto: e.target.value }))} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.possuiSupervisao}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, possuiSupervisao: v, supervisorId: v ? f.supervisorId : "" }))}
                />
                <Label>Possui supervisão</Label>
              </div>
              {form.possuiSupervisao && (
                <div className="space-y-1">
                  <Label>Supervisor</Label>
                  <Select value={form.supervisorId || "none"} onValueChange={(v) => setForm((f) => ({ ...f, supervisorId: v === "none" ? "" : v }))}>
                    <SelectTrigger><SelectValue placeholder="Selecione o supervisor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {(funcionariosAtivos as any[]).filter((f: any) => f.id !== editId).map((f: any) => (
                        <SelectItem key={f.id} value={String(f.id)}>{f.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>VR/dia (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={form.vrDia} onChange={(e) => setForm((f) => ({ ...f, vrDia: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>VT/dia (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={form.vtDia} onChange={(e) => setForm((f) => ({ ...f, vtDia: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Convênio Saúde (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={form.convenioSaude} onChange={(e) => setForm((f) => ({ ...f, convenioSaude: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Convênio Odonto (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={form.convenioOdonto} onChange={(e) => setForm((f) => ({ ...f, convenioOdonto: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Início Benefício</Label>
                  <Input type="date" value={form.beneficioDataInicio} onChange={(e) => setForm((f) => ({ ...f, beneficioDataInicio: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Fim Benefício</Label>
                  <Input type="date" value={form.beneficioDataFim} onChange={(e) => setForm((f) => ({ ...f, beneficioDataFim: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Bonificação Mínima (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={form.bonificacaoMin} onChange={(e) => setForm((f) => ({ ...f, bonificacaoMin: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Bonificação Máxima (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={form.bonificacaoMax} onChange={(e) => setForm((f) => ({ ...f, bonificacaoMax: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Regra de Bonificação</Label>
                <Textarea value={form.regrasBonificacao} onChange={(e) => setForm((f) => ({ ...f, regrasBonificacao: e.target.value }))} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Auxílio Faculdade (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={form.auxilioFaculdade} onChange={(e) => setForm((f) => ({ ...f, auxilioFaculdade: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Auxílio Curso (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={form.auxilioCurso} onChange={(e) => setForm((f) => ({ ...f, auxilioCurso: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Outros Benefícios (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={form.outrosBeneficios} onChange={(e) => setForm((f) => ({ ...f, outrosBeneficios: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Descrição Outros Benefícios</Label>
                  <Input value={form.descricaoOutrosBeneficios} onChange={(e) => setForm((f) => ({ ...f, descricaoOutrosBeneficios: e.target.value }))} />
                </div>
              </div>
            </TabsContent>

            {/* Aba Outros */}
            <TabsContent value="outros" className="space-y-4">
              <div className="space-y-1">
                <Label>Observações Internas</Label>
                <Textarea value={form.observacoesInternas} onChange={(e) => setForm((f) => ({ ...f, observacoesInternas: e.target.value }))} rows={4} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Horário Entrada</Label>
                  <Input type="time" value={form.horarioEntrada} onChange={(e) => setForm((f) => ({ ...f, horarioEntrada: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Horário Saída</Label>
                  <Input type="time" value={form.horarioSaida} onChange={(e) => setForm((f) => ({ ...f, horarioSaida: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Início Almoço</Label>
                  <Input type="time" value={form.horarioInicioAlmoco} onChange={(e) => setForm((f) => ({ ...f, horarioInicioAlmoco: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Fim Almoço</Label>
                  <Input type="time" value={form.horarioFimAlmoco} onChange={(e) => setForm((f) => ({ ...f, horarioFimAlmoco: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Carga Horária Diária (horas)</Label>
                <Input type="number" step="0.5" min="0" max="24" value={form.cargaHorariaDiaria} onChange={(e) => setForm((f) => ({ ...f, cargaHorariaDiaria: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Dias de Trabalho</Label>
                <p className="text-xs text-muted-foreground">Selecione os dias que devem gerar jornada prevista no controle de ponto.</p>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {[[0, "Dom"], [1, "Seg"], [2, "Ter"], [3, "Qua"], [4, "Qui"], [5, "Sex"], [6, "Sáb"]].map(([dia, label]) => (
                    <label key={dia} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.diasTrabalho.includes(dia as number)}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          diasTrabalho: e.target.checked
                            ? [...f.diasTrabalho, dia as number].sort((a, b) => a - b)
                            : f.diasTrabalho.filter((item) => item !== dia),
                        }))}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={fecharModalForm} disabled={isBusy}>
              <X className="w-4 h-4 mr-1" /> Cancelar
            </Button>
            <Button onClick={handleSalvar} disabled={isBusy}>
              {isBusy ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Detalhe */}
      <Dialog open={modalDetalheOpen} onOpenChange={(v) => { if (!v) fecharDetalhe(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="break-words">{detalhe?.nome ?? "Funcionário"}</DialogTitle>
          </DialogHeader>
          {detalhe && (
            <div className="text-sm text-muted-foreground mb-2 flex gap-4 flex-wrap">
              <span>Matrícula: <span className="font-mono text-foreground">{detalhe.matricula}</span></span>
              <span>Status: <Badge variant={detalhe.status === "ativo" ? "default" : "secondary"}>{detalhe.status === "ativo" ? "Ativo" : "Inativo"}</Badge></span>
              {detalhe.cargoNome && <span>Cargo: <span className="text-foreground">{detalhe.cargoNome}</span></span>}
            </div>
          )}
          <Tabs defaultValue="resumo">
            <TabsList>
              <TabsTrigger value="resumo">Resumo</TabsTrigger>
              <TabsTrigger value="arquivos">Arquivos ({(arquivos as any[]).length})</TabsTrigger>
              <TabsTrigger value="historico">Histórico ({(historico as any[]).length})</TabsTrigger>
            </TabsList>

            <TabsContent value="resumo" className="space-y-3 pt-3">
              {detalhe && (
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-muted-foreground">CPF:</span> {detalhe.cpf || "—"}</div>
                  <div><span className="text-muted-foreground">RG:</span> {detalhe.rg || "—"}</div>
                  <div><span className="text-muted-foreground">PIS:</span> {detalhe.pis || "—"}</div>
                  <div><span className="text-muted-foreground">Nascimento:</span> {formatDate(detalhe.dataNascimento)}</div>
                  <div><span className="text-muted-foreground">Admissão:</span> {formatDate(detalhe.dataAdmissao)}</div>
                  {detalhe.dataDesligamento && <div><span className="text-muted-foreground">Desligamento:</span> {formatDate(detalhe.dataDesligamento)}</div>}
                  <div><span className="text-muted-foreground">Setor:</span> {detalhe.setor || "—"}</div>
                  <div><span className="text-muted-foreground">Centro de Custo:</span> {detalhe.centroCusto || "—"}</div>
                  <div><span className="text-muted-foreground">Supervisor:</span> {detalhe.supervisorNome || "—"}</div>
                  <div><span className="text-muted-foreground">Salário:</span> {formatCurrency(detalhe.cargoSalario)}</div>
                  <div><span className="text-muted-foreground">VR/dia:</span> {formatCurrency(detalhe.vrDia)}</div>
                  <div><span className="text-muted-foreground">VT/dia:</span> {formatCurrency(detalhe.vtDia)}</div>
                  <div><span className="text-muted-foreground">Conv. Saúde:</span> {formatCurrency(detalhe.convenioSaude)}</div>
                  <div><span className="text-muted-foreground">Conv. Odonto:</span> {formatCurrency(detalhe.convenioOdonto)}</div>
                  <div><span className="text-muted-foreground">Aux. Faculdade:</span> {formatCurrency(detalhe.auxilioFaculdade)}</div>
                  <div><span className="text-muted-foreground">Aux. Curso:</span> {formatCurrency(detalhe.auxilioCurso)}</div>
                  <div><span className="text-muted-foreground">Outros Benef.:</span> {formatCurrency(detalhe.outrosBeneficios)}</div>
                  {detalhe.descricaoOutrosBeneficios && <div className="col-span-2"><span className="text-muted-foreground">Desc. Outros:</span> {detalhe.descricaoOutrosBeneficios}</div>}
                  {detalhe.observacoesInternas && <div className="col-span-2"><span className="text-muted-foreground">Obs. Internas:</span> {detalhe.observacoesInternas}</div>}
                  <div className="col-span-2 border-t pt-2 text-xs text-muted-foreground">
                    Criado por {detalhe.criadoPorNome || "—"} em {formatDate(detalhe.criadoEm)} · Atualizado por {detalhe.atualizadoPorNome || "—"} em {formatDate(detalhe.atualizadoEm)}
                  </div>
                </div>
              )}
              <div className="flex justify-end pt-2">
                <Button size="sm" onClick={() => { fecharDetalhe(); if (detalhe) abrirEditar(detalhe); }}>
                  <Pencil className="w-4 h-4 mr-1" /> Editar
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="arquivos" className="space-y-3 pt-3">
              <div className="flex items-center gap-3">
                <Select value={uploadTipo} onValueChange={setUploadTipo}>
                  <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TIPO_ARQUIVO_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  <Upload className="w-4 h-4 mr-1" /> {uploading ? "Enviando..." : "Enviar Arquivo"}
                </Button>
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleUploadArquivo} />
              </div>
              {(arquivos as any[]).length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Nenhum arquivo</p>
              ) : (
                <div className="space-y-2">
                  {(arquivos as any[]).map((a: any) => (
                    <div key={a.id} className="flex items-center justify-between border rounded p-2 text-sm">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{a.nome}</div>
                          <div className="text-xs text-muted-foreground">
                            {TIPO_ARQUIVO_LABELS[a.tipo] ?? a.tipo} · {a.tamanho ? `${(a.tamanho / 1024).toFixed(1)} KB` : "—"} · {formatDate(a.criadoEm)}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" asChild>
                          <a href={a.url} target="_blank" rel="noopener noreferrer">Abrir</a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteArquivoMut.mutate({ id: a.id, funcionarioId: detalheId! })}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="historico" className="pt-3">
              {(historico as any[]).length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Nenhuma alteração registrada</p>
              ) : (
                <div className="space-y-2">
                  {(historico as any[]).map((h: any) => (
                    <div key={h.id} className="border rounded p-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{CAMPOS_HISTORICO_LABELS[h.campo] ?? h.campo}</span>
                        <span className="text-xs text-muted-foreground">{formatDate(h.alteradoEm)} · {h.alteradoPorNome || "—"}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        <span className="line-through">{h.valorAnterior ?? "—"}</span>
                        {" → "}
                        <span className="text-foreground">{h.valorNovo ?? "—"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
